import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Users, TrendingUp, Clock, CalendarClock, ArrowRight,
} from 'lucide-react';
import Modal from './Modal';
import { analyticsAPI } from '../../services/api';
import { useAppContext } from '../../context/AppContext';
import './ModuleSummaryModal.css';

/**
 * Compact module analytics preview shown when clicking a module row.
 * `moduleRef` accepts either a numeric id, dashless UUID, or dashed UUID.
 */
const ModuleSummaryModal = ({ isOpen, onClose, moduleRef }) => {
  const navigate = useNavigate();
  const { showNotification } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !moduleRef) {
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    analyticsAPI.getModuleAnalytics(moduleRef)
      .then((response) => {
        if (cancelled) return;
        if (response.data.success) {
          setData(response.data.data);
        } else {
          setError('Failed to load module analytics');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err.response?.data?.error || 'Failed to load module analytics';
        setError(msg);
        showNotification(msg, 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, moduleRef, showNotification]);

  const dashless = (uuid) => (typeof uuid === 'string' ? uuid.replace(/-/g, '') : null);

  const goToFullPage = () => {
    if (!data) return;
    const ref = dashless(data.module.moduleId) || data.module.id;
    onClose();
    navigate(`/analytics/module/${ref}`);
  };

  const renderBody = () => {
    if (loading) {
      return <p style={{ textAlign: 'center', padding: 'var(--space-5)', color: 'var(--text-secondary)' }}>Loading…</p>;
    }
    if (error || !data) {
      return <p style={{ textAlign: 'center', padding: 'var(--space-5)', color: 'var(--text-secondary)' }}>{error || 'No data available.'}</p>;
    }

    const m = data.module;
    const topTrainees = (data.traineeProgress || [])
      .slice()
      .sort((a, b) => (b.progress || 0) - (a.progress || 0))
      .slice(0, 5);

    return (
      <div className="module-summary">
        <div className="module-summary__meta">
          {m.category && <span className="badge badge-dark">{m.category}</span>}
          {m.level && <span className="badge badge-gray" style={{ marginLeft: 6 }}>{m.level}</span>}
          <span className={`badge ${m.status === 'Active' ? 'badge-green' : 'badge-gray'}`} style={{ marginLeft: 6 }}>
            {m.status}
          </span>
          {m.duration ? <span style={{ marginLeft: 8, color: 'var(--text-secondary)' }}>· {m.duration} min</span> : null}
        </div>

        {m.description && (
          <p className="module-summary__desc">{m.description}</p>
        )}

        <div className="module-summary__stats">
          {[
            { icon: Users, label: 'Enrolled', value: m.enrolledCount, sub: `${m.completedCount} completed · ${m.inProgressCount} in progress` },
            { icon: TrendingUp, label: 'Completion', value: `${m.completionRate}%`, sub: `Avg progress ${m.averageProgress}%` },
            { icon: Clock, label: 'Total Time', value: `${m.totalTimeSpentHours}h`, sub: null },
            { icon: CalendarClock, label: 'Last Activity', value: m.lastActivity ? new Date(m.lastActivity).toLocaleDateString() : '—', sub: null },
          ].map(({ icon: Icon, label, value, sub }) => (
            <div key={label} className="module-summary__stat">
              <Icon size={16} className="module-summary__stat-icon" />
              <div className="module-summary__stat-body">
                <span className="module-summary__stat-value">{value}</span>
                <span className="module-summary__stat-label">{label}</span>
                {sub && <span className="module-summary__stat-sub">{sub}</span>}
              </div>
            </div>
          ))}
        </div>

        {topTrainees.length > 0 && (
          <div className="module-summary__top">
            <div className="module-summary__top-header">
              <span>Top performers</span>
              <span className="badge badge-dark">{data.traineeProgress.length} enrolled</span>
            </div>
            {topTrainees.map((t, idx) => (
              <div key={t.traineeId} className="module-summary__trainee">
                <span className="module-summary__rank">#{idx + 1}</span>
                <span className="module-summary__trainee-name">{t.name}</span>
                <div className="progress-bar" style={{ height: 6, flex: 1 }}>
                  <div className="progress-bar-fill" style={{ width: `${t.progress}%` }} />
                </div>
                <span className="module-summary__trainee-pct">{t.progress}%</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={data?.module?.title || 'Module Analytics'}
      footer={
        <>
          <button className="btn btn-outline" onClick={onClose}>Close</button>
          <button
            className="btn btn-primary"
            onClick={goToFullPage}
            disabled={!data}
          >
            View Full Analytics <ArrowRight size={14} style={{ marginLeft: 4 }} />
          </button>
        </>
      }
    >
      {renderBody()}
    </Modal>
  );
};

export default ModuleSummaryModal;
